import { fetchList } from "../apis/services/CommonApiService";
import { ApiEndPoints } from "../apis/ApiEndPoints";

const ITEMS_PER_PAGE = 10;

// Booking status ids used by the reservations API (booking_status param, 0 = all).
export const BOOKING_STATUS_OPTIONS = [
  { id: 1, name: "Pending" },
  { id: 2, name: "Confirmed" },
  { id: 3, name: "In House" },
  { id: 4, name: "Checked-out" },
  { id: 5, name: "Cancelled" },
  { id: 6, name: "No Show" },
];

export async function fetchReservationsPage({
  startDate,
  endDate,
  pageNo = 1,
  searchText = "",
  searchById = false,
  bookingStatus = 0,
}) {
  const params = new URLSearchParams({
    page_no: pageNo,
    items_per_page: ITEMS_PER_PAGE,
    start_date: startDate,
    end_date: endDate,
    search_text: searchText,
    booking_status: bookingStatus || 0,
    booking_source: 0,
    search_in_booking: searchById ? 1 : 0,
    corporate_bookings: 0,
    payment_type: 0,
  });

  const url = `${ApiEndPoints?.GET_RESERVATIONS}?${params.toString()}`;
  const result = await fetchList(url);

  if (result?.status !== 200) {
    throw new Error("Failed to fetch reservations");
  }

  const rows = (result?.data?.rows || []).map(normalizeReservation);
  return {
    rows,
    totalRows: result?.data?.totalRows ?? rows.length,
    pageNo,
  };
}

function fmtMoney(v) {
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "N/A";
}

/**
 * Normalizes one raw booking row from GET /booking/reservations into
 * exactly the fields the widget shows: Booking ID, Guest Name, Phone,
 * Check-in, Check-out, Status, Nights, Amount, Room Type.
 *
 * NOTE: `phone` isn't present on userData in the sample payload (only
 * name/id/userDetail.{state_id,city_id,postal_code}) — the fallbacks
 * below cover the likely field names, but confirm the real one with
 * the backend and adjust the single line marked below.
 */
function normalizeReservation(b) {
  const roomType =
    [...new Set((b?.bookingItemsData || []).map((i) => i?.roomCategoryDetail?.roomcategory_name).filter(Boolean))].join(", ") ||
    "N/A";

  return {
    id: b?.id,
    bookingId: `#${String(b?.id ?? "").padStart(6, "0")}`,
    name: b?.userData?.name || "N/A",
    // TODO: confirm real phone field name with backend
    phone: b?.userData?.mobile || b?.userData?.phone || b?.userData?.userDetail?.mobile || "N/A",
    checkin: b?.checkin_date || "N/A",
    checkout: b?.checkout_date || "N/A",
    status: b?.bookingStatusInfo?.status || "Unknown",
    nights: b?.no_of_days ?? "N/A",
    amount: fmtMoney(b?.final_amount),
    roomType,
  };
}

// ---------------------------------------------------------------------
// Hotel details quick action — same field layout the guest bot uses,
// duplicated here (not imported) so this admin bot has zero shared
// files with chatbot/ — a change to one never touches the other.
// ---------------------------------------------------------------------
export function buildHotelInfoMessage(rawHotelDetails) {
  const raw = rawHotelDetails?.data && typeof rawHotelDetails.data === "object" ? rawHotelDetails.data : rawHotelDetails;

  if (!raw) {
    return { text: "I couldn't load the hotel's details right now — please try again in a moment.", link: null };
  }

  const lat = raw?.latitude;
  const lng = raw?.longitude;
  const mapUrl = lat && lng ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}` : null;

  const lines = [
    `${raw?.company_name || "This hotel"}${raw?.address ? " is located at:" : ""}`,
    raw?.address ? `• Address: ${raw.address}` : null,
    raw?.company_phone ? `• Phone: ${raw.company_phone}` : null,
    raw?.company_email ? `• Email: ${raw.company_email}` : null,
    raw?.website ? `• Website: ${raw.website}` : null,
  ].filter(Boolean);

  return {
    text: lines.length ? lines.join("\n") : "No hotel details on file yet.",
    link: mapUrl ? { label: "Open in Google Maps", url: mapUrl } : null,
  };
}
